import { Collapse, Form, FormInstance, InputNumber, Select, Slider, Switch } from 'antd';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { txt } from '../../../../locales/i18n';

const animationTypes = [
	{
		label: 'Fade animations',
		options: [
			{ label: 'Fade', value: 'fade' },
			{ label: 'Fade up', value: 'fade-up' },
			{ label: 'Fade down', value: 'fade-down' },
			{ label: 'Fade left', value: 'fade-left' },
			{ label: 'Fade right', value: 'fade-right' },
			{ label: 'Fade up right', value: 'fade-up-right' },
			{ label: 'Fade up left', value: 'fade-up-left' },
			{ label: 'Fade down right', value: 'fade-down-right' },
			{ label: 'Fade down left', value: 'fade-down-left' },
		],
	},

	{
		label: 'Flip animations',
		options: [
			{ label: 'Flip up', value: 'flip-up' },
			{ label: 'Flip down', value: 'flip-down' },
			{ label: 'Flip left', value: 'flip-left' },
			{ label: 'Flip right', value: 'flip-right' },
		],
	},

	{
		label: 'Slide animations',
		options: [
			{ label: 'Slide up', value: 'slide-up' },
			{ label: 'Slide own', value: 'slide-down' },
			{ label: 'Slide left', value: 'slide-left' },
			{ label: 'Slide right', value: 'slide-right' },
		],
	},

	{
		label: 'Zoom animations',
		options: [
			{ label: 'Zoom in', value: 'zoom-in' },
			{ label: 'Zoom in up', value: 'zoom-in-up' },
			{ label: 'Zoom in down', value: 'zoom-in-down' },
			{ label: 'Zoom in left', value: 'zoom-in-left' },
			{ label: 'Zoom in right', value: 'zoom-in-right' },
			{ label: 'Zoom out', value: 'zoom-out' },
			{ label: 'Zoom out up', value: 'zoom-out-up' },
			{ label: 'Zoom out down', value: 'zoom-out-down' },
			{ label: 'Zoom out left', value: 'zoom-out-left' },
			{ label: 'Zoom out right', value: 'zoom-out-right' },
		],
	},
];

const animationEases = [
	{ label: 'Linear', value: 'linear' },
	{ label: 'Ease', value: 'ease' },
	{ label: 'Ease in', value: 'ease-in' },
	{ label: 'Ease out', value: 'ease-out' },
	{ label: 'Ease in out', value: 'ease-in-out' },
	{ label: 'Ease in back', value: 'ease-in-back' },
	{ label: 'Ease out back', value: 'ease-out-back' },
	{ label: 'Ease in out back', value: 'ease-in-out-back' },
	{ label: 'Ease in sine', value: 'ease-in-sine' },
	{ label: 'Ease out sine', value: 'ease-out-sine' },
	{ label: 'Ease in out sine', value: 'ease-in-out-sine' },
	{ label: 'Ease in quad', value: 'ease-in-quad' },
	{ label: 'Ease out quad', value: 'ease-out-quad' },
	{ label: 'Ease in out quad', value: 'ease-in-out-quad' },
	{ label: 'Ease in cubic', value: 'ease-in-cubic' },
	{ label: 'Ease out cubic', value: 'ease-out-cubic' },
	{ label: 'Ease in out cubic', value: 'ease-in-out-cubic' },
	{ label: 'Ease in quart', value: 'ease-in-quart' },
	{ label: 'Ease out quart', value: 'ease-out-quart' },
	{ label: 'Ease in out quart', value: 'ease-in-out-quart' },
];

export type AnimationCollapseProps = {
	propertyKey: string;
	formRef: React.RefObject<FormInstance>;
	onSectionChange: (changedValues: any, values: any) => void;
};

export const AnimationCollapse = (props: AnimationCollapseProps) => {
	const enableSwitchRef = useRef<HTMLElement>(null);
	const [animationEnabled, setAnimationEnabled] = useState(false);

	useEffect(() => {
		const enableSwitch = enableSwitchRef.current;
		if (enableSwitch === null) return;

		const animationEnabled = enableSwitch.getAttribute('aria-checked') === 'true';
		setAnimationEnabled(animationEnabled);
	}, []);

	return (
		<CollapseStyled ghost>
			<Collapse.Panel header={txt('cms.animations')} key="animations" forceRender>
				<div className="row-container">
					<div className="form-item-container">
						<label>{txt('cms.animate')}</label>
						<Form.Item
							valuePropName="checked"
							name={[props.propertyKey, 'animation', 'enable']}
						>
							<Switch
								ref={enableSwitchRef}
								onChange={(checked) => setAnimationEnabled(checked)}
							/>
						</Form.Item>
					</div>

					<div className="form-item-container">
						<label>{txt('cms.animate exit')}</label>
						<Form.Item
							valuePropName="checked"
							name={[props.propertyKey, 'animation', 'enableExit']}
						>
							<Switch disabled={animationEnabled === false} />
						</Form.Item>
					</div>
				</div>

				<div className="row-container">
					<div className="form-item-container">
						<label>{txt('cms.type')}</label>
						<Form.Item name={[props.propertyKey, 'animation', 'type']}>
							<Select
								options={animationTypes}
								disabled={animationEnabled === false}
							/>
						</Form.Item>
					</div>

					<div className="form-item-container">
						<label>{txt('cms.ease')}</label>
						<Form.Item name={[props.propertyKey, 'animation', 'ease']}>
							<Select
								options={animationEases}
								disabled={animationEnabled === false}
							/>
						</Form.Item>
					</div>
				</div>

				<div className="row-container">
					<div className="form-item-container">
						<label>{txt('cms.duration ms')}</label>

						<div style={{ display: 'flex' }}>
							<Form.Item name={[props.propertyKey, 'animation', 'duration']} noStyle>
								<Slider
									min={50}
									max={3000}
									step={50}
									style={{ flex: 1 }}
									disabled={animationEnabled === false}
								/>
							</Form.Item>

							<Form.Item name={[props.propertyKey, 'animation', 'duration']}>
								<InputNumber
									bordered={false}
									size="small"
									min={50}
									max={3000}
									style={{ width: '6ch', background: 'none' }}
									disabled
								/>
							</Form.Item>
						</div>
					</div>

					<div className="form-item-container">
						<label>{txt('cms.delay ms')}</label>

						<div style={{ display: 'flex' }}>
							<Form.Item name={[props.propertyKey, 'animation', 'delay']} noStyle>
								<Slider
									min={0}
									max={3000}
									step={50}
									style={{ flex: 1 }}
									disabled={animationEnabled === false}
								/>
							</Form.Item>

							<Form.Item name={[props.propertyKey, 'animation', 'delay']}>
								<InputNumber
									bordered={false}
									size="small"
									min={0}
									max={3000}
									style={{ width: '6ch', background: 'none' }}
									disabled
								/>
							</Form.Item>
						</div>
					</div>
				</div>
			</Collapse.Panel>
		</CollapseStyled>
	);
};

const CollapseStyled = styled(Collapse)`
	padding: 0 !important;

	.ant-collapse-content-box {
		padding: 0 !important;
	}

	.row-container {
		display: flex;
		gap: 2ch;
	}

	.form-item-container {
		flex: 1;
	}
`;
